/**
 * THE TERMS OF SERVICE, WRITTEN ONCE (PV5a) — prest0.ai/terms-of-service and onboarding's in-flow terms popup, in today's
 * design (src/components/legal/TermsContent.tsx, which both render), and in the new design's reading page
 * (src/design/pavilion/pages/terms-of-service/). See ./legalDocument.ts for how a document is shared.
 *
 * Moved here from TermsContent.tsx as written, part names aside; the terms page and the popup's rendered HTML were
 * compared byte for byte before and after the move. The date they took effect is src/config/legalVersions.ts's
 * (EffectiveDate), and a no-JavaScript copy is published as public/terms-of-service.html (kept by hand;
 * scripts/check-legal-effective-date.mjs explains why).
 */
import React from 'react';
import type { LegalDocument, LegalParts } from './legalDocument';
import { UNPAID_DAYS_BEFORE_END } from '../../config/billingPause';
import { MONTHLY_WU_PER_PRESTON, STANDARD_RATE_USD_PER_WU } from '../../config/workUnits';
import { DAY_MS, TRIAL_WARNING_LEAD_MS } from '../../types/trialConversion';
import { INTERNSHIP, INTERNSHIP_WU, INTERNSHIP_WU_PER_MONTH, formatTermsLength } from '../../config/trialTerms';

/** What the terms page says above the document. */
export const TERMS_OF_SERVICE_HEAD = {
  heading: 'terms of service',
  /** The contents the new design's reading page draws (today's page has none). */
  contentsTitle: 'on this page',
  contentsName: 'terms of service table of contents',
} as const;

export type TermsOfServiceParts = Pick<
  LegalParts,
  'Anchor' | 'Highlight' | 'HighlightIcon' | 'HighlightText' | 'HighlightTitle' | 'Highlights' | 'List' | 'ListItem' | 'OrderedList' | 'OrderedListItem' | 'OutLink' | 'PageLink' | 'SubTitle' | 'Text'
>;

/** The terms of service, in the parts the caller draws them with. */
export function termsOfServiceDocument({
  Anchor, Highlight, HighlightIcon, HighlightText, HighlightTitle, Highlights, List, ListItem, OrderedList, OrderedListItem, OutLink, PageLink, SubTitle, Text,
}: TermsOfServiceParts): LegalDocument {
  return {
    intro: (
      <>
        <Text>
          Welcome to prest0. These terms of service ("terms") explain the rules that apply when you interact with our website, platform, and AI agent tooling. Please read everything carefully before you create an account or continue using the platform.
        </Text>
        <Text>
          By accessing the platform or creating an account you agree to these terms, our{' '}
          <PageLink to="/privacy-policy">privacy policy</PageLink>, and our{' '}
          <PageLink to="/cookie-policy">cookie policy</PageLink>. If you disagree with any part of the terms, you must discontinue use of the platform immediately.
        </Text>
        <Text>
          The operational safeguards referenced throughout these terms mirror the controls described on the security page—TLS in transit, AES-256 encryption at rest on Google Cloud, sign-in through Google or an email and password with permissions resolved per person, a no-training commitment whose enforcement is being built, documented retention schedules with deletion on request, and append-only audit logs. Google Cloud's SOC 2 Type II and ISO/IEC 27001 reports cover the infrastructure underneath prest0; they are Google's reports, not prest0's.
        </Text>

        <Highlights>
          <Highlight>
            <HighlightIcon>🤝</HighlightIcon>
            <HighlightTitle>clear accountability</HighlightTitle>
            <HighlightText>
              You direct the AI agents and automations you set up. Keep their data lawful, respect third-party rights, and stay transparent with anyone who interacts with them.
            </HighlightText>
          </Highlight>
          <Highlight>
          <HighlightIcon>🔐</HighlightIcon>
          <HighlightTitle>documented security</HighlightTitle>
          <HighlightText>
            TLS in transit, AES-256 on Google Cloud, and permissions resolved per person and per message apply to every workspace. Where our own audits stand is published on the security page.
          </HighlightText>
          </Highlight>
          <Highlight>
            <HighlightIcon>🚀</HighlightIcon>
          <HighlightTitle>no-training commitment</HighlightTitle>
          <HighlightText>
            We are committed to prompts, transcripts and attachments never training shared models, with enforcement at every provider being built (targeted for October 31, 2026), and deletion on request keeps custody in your hands.
          </HighlightText>
          </Highlight>
        </Highlights>
      </>
    ),
    sections: [
      {
        id: 'acceptance',
        heading: 'acceptance of terms',
        body: (
          <>
            <Text>
              These terms constitute a binding agreement between you and prest0. If you are using the platform on behalf of an organization, you represent that you have authority to bind that organization and the term "you" extends to that entity.
            </Text>
          </>
        ),
      },
      {
        id: 'eligibility',
        heading: 'eligibility',
        body: (
          <>
            <Text>
              You must be at least 18 years old and legally capable of entering into contracts to create an account. You may not use the platform if you are barred from doing so under any applicable law or if export-control rules prohibit access from your jurisdiction.
            </Text>
          </>
        ),
      },
      {
        id: 'accounts',
        heading: 'account creation and security',
        body: (
          <>
            <Text>
              When you create an account you agree to:
            </Text>
            <List>
              <ListItem>provide accurate, current, and complete registration information;</ListItem>
              <ListItem>maintain the security of your credentials and promptly update them if compromised;</ListItem>
              <ListItem>limit access to your account to yourself or authorized team members; and</ListItem>
              <ListItem>keep two-step verification on for the Google account you use where you sign in with Google, and two-step sign-in inside prest0 on where your workspace requires it (two-step sign-in with an authenticator app is available to every member); and</ListItem>
              <ListItem>notify us immediately if you suspect unauthorized access or other security incidents.</ListItem>
            </List>
            <Text>
              You are responsible for all activity that occurs under your account, including actions taken by invited collaborators. We commit to notify you within 48 hours of confirming a security incident that affects your workspace, with what happened, what data and which matters were involved, what we did, and what you should do. The notice goes to the workspace's owners by email and in the app, in their own language, and a check runs every hour until every affected workspace has received it.
            </Text>
          </>
        ),
      },
      {
        id: 'platform-use',
        heading: 'platform access and updates',
        body: (
          <>
            <Text>
              We continually improve the platform. Features may change, be discontinued, or move to different plans without notice. We strive to provide advance communication for impactful changes and will use commercially reasonable efforts to avoid service interruptions during business-critical hours.
            </Text>
          </>
        ),
      },
      {
        id: 'acceptable-use',
        heading: 'acceptable use requirements',
        body: (
          <>
            <Text>
              You agree not to misuse the platform. Prohibited behavior includes, but is not limited to:
            </Text>
            <List>
              <ListItem>violating any applicable law, regulation, or industry guideline;</ListItem>
              <ListItem>impersonating any person or entity or misrepresenting your affiliation with prest0;</ListItem>
              <ListItem>probing, scanning, or testing the vulnerability of the platform without prior written consent;</ListItem>
              <ListItem>introducing malware, bots, or other harmful code;</ListItem>
              <ListItem>overloading or disrupting the infrastructure supporting the platform;</ListItem>
              <ListItem>harvesting or scraping information, including personal data, without prior authorization; and</ListItem>
              <ListItem>using the platform to generate or disseminate content that is deceptive, exploitative, or likely to cause harm.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'security',
        heading: 'security standards',
        body: (
          <>
            <Text>
              The technical safeguards described on our security page apply to every customer workspace. By using the platform you acknowledge and agree to the following baseline controls:
            </Text>
            <List>
              <ListItem>TLS protects all browser and API sessions, with TLS 1.3 for every browser that supports it.</ListItem>
              <ListItem>Data stored in Google Cloud Firestore and Storage is encrypted with AES-256 and managed key rotation.</ListItem>
              <ListItem>Sign-in through Google or an email and password, with role-based permissions resolved per person and per message. Two-step sign-in with an authenticator app is available to every member, and a workspace can require it for all its members.</ListItem>
              <ListItem>We are committed to keeping prompts, transcripts and attachments out of shared model training. Every request through our model gateway carries zero-data-retention and no-collection flags; no training and zero retention at every provider, fallbacks included, is targeted for October 31, 2026.</ListItem>
              <ListItem>We operate documented retention schedules and delete your data on request across storage and transcripts. A deletion request can be cancelled for 30 days; we carry it out after that window closes, and in any case within 45 days of the request, except where a legal hold or legal, tax or regulatory duties require us to keep records. Once a deletion is carried out, copies in our daily backups are cleared within 7 days. Self-service retention timers and automated deletion receipts are on the roadmap, not shipped.</ListItem>
              <ListItem>Customer production data is stored on Google Cloud in the United States: our databases (Firestore), Cloud Functions and Cloud Run services run in Google Cloud's us-central1 region (Iowa). The exceptions inside the United States are one stopped legacy Windows workstation and some disk snapshots in us-east4 (Virginia), which are moving to us-central1, and some snapshots in Google's US multi-region; Secret Manager replicates platform secrets automatically, and server logs sit in Cloud Logging's global location. Two AI providers are reached at Singapore endpoints, and authorized staff and contractors outside the United States reach our systems remotely through named accounts; our privacy policy describes each, and our current list of subprocessors, with where each runs, is on our trust page.</ListItem>
              <ListItem>Google Cloud, which runs the infrastructure underneath prest0, holds SOC 2 Type II and ISO/IEC 27001 reports for it; those reports are Google's, not prest0's. Where our own audits stand is published on the security page, and penetration testing is planned. We maintain append-only audit logs.</ListItem>
            </List>
            <Text>
              You must implement complementary safeguards (for example, strong identity management and data classification) so that your use of the platform aligns with these standards.
            </Text>
          </>
        ),
      },
      {
        id: 'ai-agents',
        heading: 'ai agent responsibilities',
        body: (
          <>
            <Text>
              Our tooling lets you direct AI agents: Prest0n, and the routines, skills and workflows you set up for it. You alone are responsible for the outputs, decisions, disclosures, and interactions of those AI agents. You agree to:
            </Text>
            <List>
              <ListItem>ensure that AI agents clearly communicate they are automated experiences when applicable;</ListItem>
              <ListItem>avoid training or deploying agents that discriminate, infringe intellectual property, or violate privacy;</ListItem>
              <ListItem>maintain accurate documentation of prompts, guardrails, and data sources used by each agent;</ListItem>
              <ListItem>respect third-party API, model, and dataset license terms that may govern the resources you connect;</ListItem>
              <ListItem>implement human oversight for decisions that could impact finances, safety, or legal obligations; and</ListItem>
              <ListItem>keep agent prompts within the platform&rsquo;s guardrails (no attempts to bypass permissions, ethical walls, legal holds, or logged execution paths); and</ListItem>
              <ListItem>comply with any additional requirements we publish for regulated or high-risk use cases.</ListItem>
            </List>

            {/*
              2026-09-11. This section was ONE paragraph — fees are due, non-refundable,
              taxes extra — with no auto-renewal disclosure, no cancellation right, no
              trial terms and no refund policy, on a product that sells 12-month
              auto-renewing subscriptions. California's Automatic Renewal Law
              (Cal. B&P §17600 et seq.) requires the renewal terms to be stated clearly
              and conspicuously BEFORE purchase, and §17603 makes the remedy for getting
              it wrong an unconditional gift — full restitution of every charge. The
              paragraphs below are the disclosure half; the machinery half is the
              21-day advance notice (maintenance/renewalNotice.ts) and the in-app
              cancellation on /subscription.
            */}
          </>
        ),
      },
      {
        id: 'payments',
        heading: 'plans, billing, and cancellation',
        body: (
          <>
            <Text>
              Some features require a paid subscription or usage-based fees. Unless a separate order form says otherwise, fees are exclusive of applicable taxes.
            </Text>

            {/*
              2026-10-30 (Justin; flagged for Miles; CLAUDE.md W-R2a): the free trial became the free internship. Its
              figures are the platform's constants (src/config/trialTerms.ts); the static copy in
              public/terms-of-service.html types them and scripts/check-billing-claims.mjs holds the two equal. Who gets
              it is decided by the server (functions/src/internship/internshipEligibility.ts), and the one-way records
              are the internship's markers (internship_guards, kept three years). The same day (per-employee pricing,
              docs/PLAN-per-employee-pricing-20261030.md, rulings R8, R10, R11, R14): a card starts the internship and
              the plan chosen at signup starts when it ends unless it is turned off, in place of "requires no credit
              card, and does not convert". The static copy says the same.
            */}
            <SubTitle>your free internship</SubTitle>
            <Text>
              A new organization&rsquo;s primary owner can take one free internship: {formatTermsLength(INTERNSHIP)} of Prest0n, with {INTERNSHIP_WU} PRO Work Units (pr0) of work credited to the organization when it starts, at an intern&rsquo;s rate of {INTERNSHIP_WU_PER_MONTH} pr0 a month. A card on file starts the internship, and nothing is charged that day. When you start it you choose a plan — how many Prest0n employees, and monthly, annual or a year paid up front — and that plan <strong>starts automatically when the internship ends unless you turn it off</strong>, in one tap on your subscription page, at any time before it ends; turning it off charges nothing and does not end the internship early. Before you agree you are shown the plan, its amount, the day it starts and how to turn it off, and no automatic charge comes sooner than {TRIAL_WARNING_LEAD_MS / DAY_MS} days after we tell you its day and its amount. You can also start the plan before the internship ends. A code may waive the card, and a person invited to an existing organization joins it without one.
            </Text>
            <Text>
              The internship is once per person. A personal workspace, and anyone who already belongs to an organization, does not get one. An invitation code or a referral link can give an extended internship in its place, on the terms the code or link states. To keep it once per person, we keep a one-way record of the email address, the device and the network an internship was taken from (never the address itself) for three years, and we may refuse an internship, or end one early, when it repeats one already taken, under another account or otherwise, or when it is not for a real new organization. Internship credit has no cash value and cannot be withdrawn or transferred.
            </Text>

            {/*
              2026-10-11 (signed-order program, Miles's decisions): what a subscription includes, how work
              is metered, that there is no grace period, and that annual is a binding 12 months whose
              cancellation turns off renewal. The numbers are the platform's own constants; the static
              copy in public/terms-of-service.html says the same, pinned by scripts/check-billing-claims.mjs.
            */}
            <SubTitle>what a subscription includes</SubTitle>
            <Text>
              Your organization's allocation is at least {MONTHLY_WU_PER_PRESTON.toLocaleString('en-US')} PRO Work Units (pr0) per Prest0n employee per month, for every month you pay for. For a founding subscription — one whose order is signed while the founding window is open, a limited-time offer for early adopters — that amount rises on the published schedule, prepaid months included, and never falls; any other subscription keeps the amount in force on the day its order is signed, for as long as it stays active. A subscription signed on an earlier price card keeps that card's allocation per seat. The allocation is shared by your whole team and is used before your wallet. Each month's pr0 stay usable through the end of the following month, and the oldest are used first. Unused allocation ends when the subscription ends. The allocation is not money: it has no cash value and cannot be withdrawn or transferred.
            </Text>

            <SubTitle>your price card</SubTitle>
            <Text>
              The price card your subscription is signed on is locked for as long as the subscription stays active, renewals included. Your price per Prest0n employee is that card's rung for your team size and term: adding employees moves your whole team down the card to the rung for its new size, and fewer employees, or a monthly term, is the same card's dearer rung. If the card we sell new customers is priced in the same unit and its rung for your size and term costs less, you pay that lower rung. The lock ends when the subscription ends; a returning customer signs on the card sold that day.
            </Text>

            <SubTitle>how work is charged</SubTitle>
            <Text>
              Work is metered. Each task is charged in pr0 for the work it actually performs, and every charge is shown task by task on your activity page. The rate card on our pricing page shows what typical tasks use, for planning; it is not a fixed price per task. Work beyond the allocation draws your wallet at the Standard Rate (${STANDARD_RATE_USD_PER_WU.toFixed(2)} per pr0).
            </Text>
            <Text>
              When the allocation and the wallet are both empty, new work does not start until you add pr0, and the step already in progress finishes. On a paid subscription, anything that step uses past zero is on us: there is no overdraft, and nothing is billed in arrears. Spending limits your organization sets also stop new work when they are reached, and they count all work, including work the allocation pays for.
            </Text>

            <SubTitle>your wallet</SubTitle>
            <Text>
              Your wallet holds pr0 you buy as top-ups, or through automatic top-up if you turn it on, at the Standard Rate plus the card transaction fee shown before you pay. Wallet balance you bought can be withdrawn to a bank account from the wallet page. Internship credit, the monthly allocation, and promotional or bonus credit have no cash value and cannot be withdrawn. Internship credit you have not used when you subscribe stays in your wallet and is used after the allocation.
            </Text>

            <SubTitle>automatic renewal</SubTitle>
            <Text>
              Paid subscriptions renew automatically until you cancel, and the payment method on file is charged your price card's rate for your plan and team size: a monthly plan every month; an annual plan, which is a binding 12-month commitment billed monthly, every month of its term, renewing for another 12 months at the end of each term; and a year paid up front, once a year. You agree to that automatic renewal when you subscribe, and the amount, the billing frequency and the renewal date are shown to you before you confirm.
            </Text>
            <Text>
              For any term of a year or longer we send you a reminder at least 15 and no more than 45 days before it renews, stating what renews, how much will be charged and how often, and how to cancel. It goes to the email on your account.
            </Text>

            <SubTitle>failed payments</SubTitle>
            <Text>
              There is no grace period. If a subscription charge fails, new work pauses at once; work already running finishes, and nothing is deleted. We tell the owner and the people who manage billing, the charge is retried, and you can pay it from your subscription page at any time. Work resumes the moment a payment succeeds. If a charge is still unpaid {UNPAID_DAYS_BEFORE_END} days after it failed, the subscription ends.
            </Text>

            <SubTitle>changing your plan</SubTitle>
            <Text>
              Adding Prest0n employees takes effect right away: your whole team moves to your price card's rung for its new size, and the rest of the current billing period is charged that day. Fewer employees, or a shorter commitment, take effect when the current term ends — at the end of the month on a monthly plan, and at the end of the 12-month term on an annual plan or a year paid up front. Moving from monthly to annual starts a new 12-month term at your next renewal.
            </Text>

            <SubTitle>cancelling</SubTitle>
            <Text>
              You can cancel at any time from your subscription page at prest0.ai/subscription — the same place and the same few clicks it took to subscribe. No phone call, no email to support, and no retention interview. Cancelling turns off renewal. A monthly plan ends at the end of the month you have paid for, and a year paid up front at the end of that year; nothing more is charged. An annual plan is a binding 12-month commitment: cancelling stops it renewing, the current term runs to its end, and its remaining monthly payments continue until then. You keep full access until the plan ends, and you can undo a cancellation until it takes effect.
            </Text>

            <SubTitle>refunds</SubTitle>
            <Text>
              Subscription fees already charged are non-refundable except where the law requires otherwise, and consumed usage is never refundable. If we charge you in error, or bill you after a cancellation that we failed to process, we refund it — tell us and we will make it right rather than pointing at this paragraph.
            </Text>

            <Text>
              If we enable monetization of your AI agents, supplemental terms covering payouts, revenue shares, and tax reporting obligations will apply and must be accepted before revenue is disbursed.
            </Text>
          </>
        ),
      },
      {
        id: 'intellectual-property',
        heading: 'intellectual property',
        body: (
          <>
            <Text>
              prest0 retains all rights, title, and interest in the platform, its look and feel, and all related intellectual property. You retain ownership of the content, prompts, workflows, and AI configurations you create. By submitting or publishing content through the platform you grant prest0 a worldwide, royalty-free license to host, display, and use that content solely to provide the services to you. We use your content only to provide, support and secure the services for you, and as you direct. We never use it to train AI models, ours or anyone else's: every request through our model gateway carries zero-data-retention and no-collection flags, and no training and zero retention at every provider, fallbacks included, is targeted for October 31, 2026. We may use aggregated usage measurements that do not contain your content or identify you or your clients to run and improve the service.
            </Text>
          </>
        ),
      },
      {
        id: 'confidentiality',
        heading: 'confidentiality and security',
        body: (
          <>
            <Text>
              Both parties agree to protect any confidential information received from the other party with the same degree of care used to safeguard their own confidential data. You may only use confidential information for purposes of evaluating or using the platform. We will continue to operate the layered controls referenced above (TLS, AES-256 on Google Cloud, role-based permissions, append-only audit logs, and planned penetration tests). You are responsible for encrypting or anonymizing any sensitive data you choose to import and for complying with all applicable breach-notification laws.
            </Text>
          </>
        ),
      },
      {
        id: 'privacy',
        heading: 'privacy and data protection',
        body: (
          <>
            <Text>
              We process personal data in accordance with our{' '}
              <PageLink to="/privacy-policy">privacy policy</PageLink> and the security commitments summarized on the security page. Those commitments include the no-training commitment, documented retention schedules with deletion on request, and our breach-notification commitment.
            </Text>
            <Text>
              You represent that you have obtained all necessary consents and have a lawful basis for any personal data you share with us through the platform. If you connect external data sources, you remain responsible for honoring your own retention and privacy obligations.
            </Text>
          </>
        ),
      },
      {
        id: 'sms-communications',
        heading: 'sms communications',
        body: (
          <>
            {/* PRE-72: /signup checkbox links to /terms-of-service#sms. Expose a
                short 'sms' anchor inside this section so the link scrolls correctly
                without renaming the existing id (which is used elsewhere). */}
            <Anchor id="sms" />
            <Text>
              prest0 may send SMS text messages to your mobile phone number as part of the services, including account notifications, case updates, and appointment reminders. By providing your phone number and opting in to SMS notifications, you agree to the following:
            </Text>
            <List>
              <ListItem>you agree to receive text messages from prest0 related to your account and services at the phone number you provide.</ListItem>
              <ListItem>standard message and data rates from your carrier may apply to all messages sent and received.</ListItem>
              <ListItem>you may opt out at any time by texting STOP in reply to any message from prest0. You will receive one final confirmation and then no further texts.</ListItem>
              <ListItem>prest0 is not responsible for delayed or undelivered messages due to carrier issues, network outages, or factors outside our control.</ListItem>
              <ListItem>you represent that the phone number you provide is assigned to you and that you are authorized to receive messages at that number. You agree to notify prest0 promptly if you change or reassign your phone number.</ListItem>
              <ListItem>consent to receive SMS messages is not a condition of purchase or use of any prest0 service.</ListItem>
              <ListItem>for help with SMS communications, reply HELP to any message or contact <OutLink href="mailto:support@prest0.ai">support@prest0.ai</OutLink>.</ListItem>
            </List>
            {/* PRE-72: additive carrier-compliance-grade consent language. */}
            <Text>
              Consent to the SMS program is captured via an explicit, unchecked-by-default
              checkbox at the point of signup or in your account settings. Your agreement
              to these terms includes the following acknowledgements:
            </Text>
            <List>
              <ListItem>the SMS consent checkbox is independent of the Terms of Service / Privacy Policy acknowledgement; ticking one does not imply the other.</ListItem>
              <ListItem>standard carrier message and data rates apply to every inbound and outbound message.</ListItem>
              <ListItem>you may opt out at any time by replying STOP; your right to opt out is absolute and cannot be waived.</ListItem>
              <ListItem>prest0 may cease sending you SMS at any time, with or without notice, for operational, legal, or abuse-prevention reasons.</ListItem>
              <ListItem>prest0 maintains an auditable consent recordkeeping system — timestamp, IP, user-agent, and opt-in/opt-out events — and keeps each consent record for 4 years from the last text sent under that consent, as A2P 10DLC, TCPA, and similar carrier-compliance rules expect.</ListItem>
              <ListItem>for the full SMS program description, including frequency, message categories, and support contact, see the dedicated <PageLink to="/sms-terms">SMS Terms</PageLink> page.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'third-parties',
        heading: 'third-party services',
        body: (
          <>
            <Text>
              The platform may connect to third-party APIs, models, datasets, or integrations. Those services are governed by their own terms. We are not responsible for the availability or performance of third-party offerings and may disable integrations that become unstable, insecure, or non-compliant.
            </Text>
          </>
        ),
      },
      {
        id: 'beta',
        heading: 'beta features and feedback',
        body: (
          <>
            <Text>
              From time to time we may invite you to test early or experimental features. Beta features are provided as-is, may be discontinued at any time, and are confidential unless we state otherwise. By providing feedback you grant prest0 the right to use and incorporate that feedback without restriction or obligation to you.
            </Text>
          </>
        ),
      },
      {
        id: 'warranty',
        heading: 'disclaimer of warranties',
        body: (
          <>
            <Text>
              The platform is provided on an "as is" and "as available" basis. prest0 expressly disclaims any warranties of merchantability, fitness for a particular purpose, title, and non-infringement. We do not guarantee uninterrupted, error-free, or completely secure operation, nor do we warrant that AI outputs will meet your expectations or comply with your intended use.
            </Text>
          </>
        ),
      },
      {
        id: 'liability',
        heading: 'limitation of liability',
        body: (
          <>
            <Text>
              To the maximum extent permitted by law, neither prest0 nor its affiliates, suppliers, or licensors will be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, or for any loss of profits, revenues, goodwill, data, or business opportunities arising from or related to your use of the platform, even if we have been advised of the possibility of such damages.
            </Text>
            <Text>
              Our aggregate liability for all claims related to the platform will not exceed the greater of (a) the amounts you paid us for the service in the twelve months preceding the event giving rise to the claim, or (b) one hundred U.S. dollars ($100).
            </Text>
          </>
        ),
      },
      {
        id: 'indemnification',
        heading: 'indemnification',
        body: (
          <>
            <Text>
              You agree to defend, indemnify, and hold harmless prest0 and our officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or expenses (including reasonable attorney fees) arising from your use of the platform, your AI agents, your violation of these terms, or your infringement of any third-party rights.
            </Text>
          </>
        ),
      },
      {
        id: 'suspension',
        heading: 'suspension and termination',
        body: (
          <>
            <Text>
              We may suspend or terminate access to the platform immediately and without notice if we believe you have violated these terms, created risk or possible legal exposure for prest0, or if we are required to do so by law. Upon termination you must cease all use of the platform and delete any confidential information obtained through it.
            </Text>
          </>
        ),
      },
      {
        id: 'compliance',
        heading: 'compliance with laws',
        body: (
          <>
            <Text>
              You will comply with all applicable export control and sanctions laws, anti-corruption laws, and data protection regulations. You agree not to use the platform for unlawful surveillance, discrimination, or to create content that violates intellectual property, privacy, or publicity rights.
            </Text>
          </>
        ),
      },
      {
        id: 'disputes',
        heading: 'dispute resolution',
        body: (
          <>
            <Text>
              We prefer to resolve issues informally. Before filing a formal claim, please contact us so we can attempt to resolve the matter together. If we cannot resolve a dispute within thirty (30) days, either party may pursue remedies available under applicable law.
            </Text>
            <OrderedList>
              <OrderedListItem>send us written notice of the dispute that describes the issue and the relief sought;</OrderedListItem>
              <OrderedListItem>work with us in good faith to resolve the dispute informally; and</OrderedListItem>
              <OrderedListItem>if unresolved after 30 days, pursue mediation, arbitration, or litigation in accordance with governing law.</OrderedListItem>
            </OrderedList>
          </>
        ),
      },
      {
        id: 'governing-law',
        heading: 'governing law and venue',
        body: (
          <>
            <Text>
              These terms are governed by the laws specified in your order form or, if none is specified, the laws that apply to prest0’s corporate domicile, without regard to conflict-of-law principles. Any disputes arising under these terms will be resolved exclusively in the competent courts associated with that governing law, and both parties consent to the jurisdiction of those courts.
            </Text>
          </>
        ),
      },
      {
        id: 'changes',
        heading: 'changes to these terms',
        body: (
          <>
            <Text>
              When we change these terms, we tell you in the product and ask you to accept the new version; it applies to you from when you accept it, and until then the version you last accepted keeps applying. The app asks you to accept before you can keep using it; you can instead sign out, and our support team (<OutLink href="mailto:support@prest0.ai">support@prest0.ai</OutLink>) will export your data or close your account on request. For a change that materially reduces your rights, we give you at least thirty (30) days' notice before we ask, unless the change is required by law or to address a security risk.
            </Text>
          </>
        ),
      },
      {
        id: 'contact',
        heading: 'contact us',
        body: (
          <>
            <Text>
              Questions about these terms? Email <OutLink href="mailto:support@prest0.ai">support@prest0.ai</OutLink>.
            </Text>
          </>
        ),
      },
    ],
  };
}
